/**
 * Canonical LensReport contract.
 *
 * Owned by the Lens Engine. Backend builds it. Frontend renders it.
 * Nobody copies or partially re-models these interfaces.
 *
 * Layers: Contract (this file) → Validation (Zod) → Transport (lensApi) → Rendering.
 */

export type AssessmentMethod = 'deterministic' | 'llm' | 'hybrid'
export type {
  LensReportV3,
  LensReportV3DisplayCopy,
  LensReportV3Issue,
  LensReportV3Recommendation,
  LensReportV3Strength,
} from './report-v3'
export type VoiceDimensionKey = 'visibility' | 'orientation' | 'inclusion' | 'clarity' | 'execution'
export type ConfidenceLevel = 'high' | 'moderate' | 'low'
export type ImpactTier = 'high' | 'medium' | 'low'
export type FindingSeverity = 'critical' | 'high' | 'medium' | 'low'

export const VOICE_DIMENSION_KEYS: readonly VoiceDimensionKey[] = [
  'visibility',
  'orientation',
  'inclusion',
  'clarity',
  'execution',
] as const

export type { DimensionScore, RatingState } from './score-state'

export {
  SCORE_NOT_APPLICABLE,
  SCORE_NOT_ASSESSABLE,
  assessedScore,
  assessedValue,
  isAssessedScore,
  parseDimensionScore,
  persistScore,
} from './score-state'

// ---------------------------------------------------------------------------
// Finding
// ---------------------------------------------------------------------------

export type ImpactFrame = 'barrier' | 'friction' | 'drag' | 'working'

export const IMPACT_FRAME_ORDER: Record<ImpactFrame, number> = {
  barrier: 0,
  friction: 1,
  drag: 2,
  working: 3,
}

export interface OutputToneProfile {
  register: 'clinical' | 'professional' | 'conversational' | 'coaching'
  formality: number
  directness: number
  readerAddress: 'second_person' | 'third_person' | 'passive'
}

export interface ScoreImpact {
  characteristicScore: number
  dimensionPointsLost: number
  impactTier: ImpactTier
}

/** Display-ready location for a finding — engine-built; frontend never invents. */
export interface FindingLocation {
  /** 1-based page number; null when single-page or unknown */
  pageNumber: number | null
  /** URL pathname or page label; null for paste/file without URL */
  pagePath: string | null
  /** Section heading/name when known */
  sectionName: string | null
  /** 1-based section index when known */
  sectionIndex: number | null
  /** 1-based paragraph index when known */
  paragraphIndex: number | null
  /** Engine-built label, e.g. "Page 2 · /careers · Section 3" */
  displayLabel: string | null
  /**
   * Stable corpus block id from the id-stamped reading sample.
   * Optional for legacy reports; when present, display still uses displayLabel —
   * the frontend never invents page/section from blockId.
   */
  blockId?: string | null
}

export interface LensFinding {
  id: string
  dimension: VoiceDimensionKey
  characteristicKey: string
  /**
   * Derived from the characteristic score — never hardcoded.
   *
   * `null` exactly when the finding is advisory, because there is no score to
   * derive it from. Paired with `scoreImpact`: both null, or neither.
   */
  severity: FindingSeverity | null
  confidence: ConfidenceLevel
  title: string
  description: string
  /** null when impact is unavailable */
  whyItMatters: string | null | null
  /** null when fix is unavailable */
  suggestedFix: string | null
  /**
   * @deprecated Prefer location.sectionName. Kept in sync for one release.
   * null when unknown
   */
  sectionName: string | null
  /** null when unavailable */
  flaggedExcerpt: string | null
  /**
   * Display-ready fix prompt for clipboard UX.
   * null when unavailable — frontend never invents this.
   */
  fixPrompt: string | null
  /**
   * What this finding cost the dimension score.
   *
   * `null` exactly when the finding is advisory — observed and reported, but
   * contributing to no characteristic score. `severity` is null on the same
   * findings and only those; the two are never independently null.
   */
  scoreImpact: ScoreImpact | null
  /** Where the issue appears — null when completely unknown */
  location: FindingLocation | null

  /** v7 — reader-impact severity frame (absent on v6 reports). */
  impactFrame?: ImpactFrame
  /** v7 — hedged reader-impact description for this finding. */
  readerImpact?: string
}

// ---------------------------------------------------------------------------
// Analysis Quality
// ---------------------------------------------------------------------------

export interface MissingCharacteristic {
  key: string
  reason: 'llm_omitted' | 'llm_truncated' | 'not_applicable' | 'content_insufficient'
}

export interface AnalysisQuality {
  completeness: 'complete' | 'partial' | 'abbreviated'
  wordCount: number
  characteristicsScored: number
  characteristicsExpected: number
  missingCharacteristics: MissingCharacteristic[]
  llmTruncated: boolean

  engineVersion: string
  model: string
  extractionMethod: 'scrapingbee' | 'direct_fetch' | 'paste' | 'file_upload' | 'unknown'
  unparsedSections: number
  /** Honest limitations (images detected but not analyzed, etc.). */
  limitations?: string[]
}

// ---------------------------------------------------------------------------
// Score tier
// ---------------------------------------------------------------------------

export interface ScoreTier {
  label: string
  /** Semantic token — not a CSS class. Frontend maps to visuals. */
  colorToken: string
}

// ---------------------------------------------------------------------------
// Dimension report — semantic display data included
// ---------------------------------------------------------------------------

export interface CharacteristicScore {
  key: string
  label: string
  dimension: VoiceDimensionKey
  score: number
  isBoolean: boolean
  method: AssessmentMethod
  evidence?: string
  findingTitle?: string
  improvement?: string
  sectionName?: string
  flaggedExcerpt?: string
  confidence?: ConfidenceLevel
}

export interface DimensionReport {
  key: VoiceDimensionKey
  letter: string
  name: string
  definition: string
  score: import('./score-state').DimensionScore
  tier: ScoreTier
  assessmentSummary: string | null
  strengths: string[]
  opportunities: string[]
  findingIds: string[]
  characteristics: CharacteristicScore[]

  /** v7 — hedged reader-experience description for this dimension. */
  readerExperience?: string | null
  /** v7 — reader-impact severity frame for this dimension. */
  impactFrame?: ImpactFrame
}

// ---------------------------------------------------------------------------
// Scores / metadata / summary
// ---------------------------------------------------------------------------

export interface VoiceScores {
  visibility: import('./score-state').DimensionScore
  orientation: import('./score-state').DimensionScore
  inclusion: import('./score-state').DimensionScore
  clarity: import('./score-state').DimensionScore
  execution: import('./score-state').DimensionScore
}

export interface LensReportMetadata {
  assessmentId: string
  engineVersion: string
  profileId: string
  analyzedAt: string
  contentType: string
  sourceUrl: string | null
  domain: string | null
  assessmentType: 'single' | 'website_scan'
  pagesAnalyzed: number
  contentTitle: string | null
  audience: string | null
  detectedIntent?: string | null
  actionRequired?: boolean | null
  /** Semantic report type label for masthead (e.g. "Content Analysis"). */
  reportTypeLabel?: string | null
  /** v7 — output tone profile resolved from content type + audience. */
  toneProfile?: OutputToneProfile
}

export interface LensReportSummary {
  /**
   * Legacy field — optional for old persisted reports only.
   * New reports write executiveSummary only.
   */
  assessment?: string | null
  executiveSummary: string | null
  /** v7 — reader-impact executive summary (~2–4 sentences, hedged). */
  readerImpactSummary?: string | null
}

export type RatingLevel = 'very_high' | 'high' | 'medium' | 'low' | 'very_low'

export interface LensReportRatings {
  understanding: import('./score-state').RatingState
  execution: import('./score-state').RatingState | null
  understandingScore: number | null
  executionScore: number | null
}

export interface ThingDoneWell {
  title: string
  example: string
  dimension: VoiceDimensionKey
  contributionScore: number
}

export interface ImprovementIdea {
  title: string
  example: string
  dimension: VoiceDimensionKey
  predictedImpact: number
}

export interface TopAction {
  recommendationId: string
  /**
   * Characteristics this action addresses. Links an action to the findings it
   * fixes — page scoping depends on it. Absent on reports stored before the
   * field existed; readers fall back to parsing `recommendationId`.
   */
  characteristicKeys?: string[]
  title: string
  whatToChange: string
  whyItMatters: string | null
  impactTier: ImpactTier
  effort: 'low' | 'medium' | 'high' | null
  dimensions: VoiceDimensionKey[]
  /**
   * KI-032 D1: `barrier` when absence/quality blocks the reader; `enhancement`
   * when the feature is beneficial but not required. Optional for older reports.
   */
  nature?: 'barrier' | 'enhancement'
}

/**
 * A gap the author-input gate held back from `topActions` because acting on
 * it needs a fact the document does not contain (an owner's name, a
 * deadline, a contact route — see `openQuestionService.ts` in the engine).
 * A rewriter cannot invent this; only the author can supply it.
 *
 * Always populated when such a gap exists, whether or not this caller also
 * has a live turn to ask about it (the MCP does; the web app does not yet).
 * A report with an empty `topActions` and a non-empty `authorInputFindings`
 * is not an empty report — render this list so the gap is not silently
 * dropped. See KI-032 requirement B.
 */
export interface AuthorInputFinding {
  characteristicKey: string
  dimension: VoiceDimensionKey
  /** Plain-language question to put to the author, e.g. "Who is responsible for this?" */
  question: string
  /** Why Lens flagged this — the evidence/observation behind the gap. */
  evidence: string
  currentScore: number
}

// ---------------------------------------------------------------------------
// Prevalence (multi-page cross-page analysis)
// ---------------------------------------------------------------------------

export type PrevalenceLevel = 'universal' | 'widespread' | 'isolated'

export interface CharacteristicPrevalence {
  key: string
  label: string
  dimension: VoiceDimensionKey
  level: PrevalenceLevel
  pageCount: number
  totalPages: number
}

/**
 * Page-scoped slice of a multi-page report.
 * Part of LensReport — not a nested LensReport.
 */
export interface PageReport {
  url: string | null
  pageType: string | null
  /** Display label for tabs (path or hostname). */
  label: string
  caiScore: import('./score-state').DimensionScore
  tier: ScoreTier
  dimensionScores: VoiceScores
  findingIds: string[]
  wordCount: number
  sourceText?: string | null
  /** Per-page deterministic ratings (populated when per-page pipeline runs). */
  ratings?: LensReportRatings
  /** Per-page selected recommendations count (teaser — actual recs behind license gate). */
  recommendationCount?: number
  /** Per-page structural message when no tier improvement possible. */
  structuralMessage?: string | null
  /**
   * Engine-assembled rewrite prompt scoped to this page.
   * Browser reads only — never invents when missing.
   */
  unifiedFixPrompt?: string | null
  /** Why this page has no prompt. Null when it has one. */
  unifiedFixPromptUnavailable?: UnifiedFixPromptUnavailable | null
}

/**
 * Why no revision prompt was assembled.
 *
 * An absent `unifiedFixPrompt` on its own is indistinguishable from a document
 * with nothing to fix, and the client rendered both as a missing button. This
 * names which it was.
 *
 * `message` is the only reader-facing part and deliberately carries no counts,
 * characteristic names or drop reasons — DEP-034 removed candidate diagnostics
 * from reader surfaces.
 */
export interface UnifiedFixPromptUnavailable {
  reason: 'no_findings' | 'all_candidates_dropped' | 'all_amendments_dropped'
  message: string
}

export type {
  LensReportV2,
  LensReportV2Scores,
  LensReportFix,
  LensReportCoverage,
  ReportCharacteristicScore,
  StoredReport,
  CharacteristicScoreState,
  CharacteristicScoreReason,
} from './report-v2'

export { readStoredReport, unwrapStoredReportRoot } from './report-v2'

export type {
  LensObservation,
  ReportObservation,
  EvidenceMode,
  ObservationKind,
  ObservationSeverity,
} from './observations'

// ---------------------------------------------------------------------------
// Comparison (optimization loop)
// ---------------------------------------------------------------------------

export interface ScoreDeltaEntry {
  before: number | null
  after: number | null
  delta: number | null
  status?: 'not-assessed'
}

export interface PredictionDeltaEntry {
  before: RatingLevel | 'not_assessable'
  after: RatingLevel | 'not_assessable'
  improved: boolean
}

/**
 * Per-characteristic change classification.
 *
 * - `improved`   — same characteristic, higher score on child (score change 1-3 range)
 * - `regressed`  — same characteristic, lower score on child, including the
 *                  "was ≥4 strength, now ≤3 finding" case
 * - `resolved`   — parent flagged (≤3) and child now scores ≥4
 * - `unchanged`  — same score
 * - `new`        — characteristic wasn't scored on parent at all (weight 0 / profile change);
 *                  UI surfaces "wasn't checked on your previous version" flag
 * - `scoring-gap`— explicit alias of `new`, kept separate so the UI can render a
 *                  stronger "why wasn't this flagged before?" affordance
 * - `not-assessed` — characteristic was present on the parent and absent from the child.
 *                    Not a resolved issue; the newer run did not assess it.
 */
export type CharacteristicChangeStatus =
  'improved' | 'regressed' | 'resolved' | 'unchanged' | 'new' | 'scoring-gap' | 'not-assessed'

export interface CharacteristicChange {
  /** Characteristic key (e.g. `reading_level`). */
  key: string
  /** Human label (e.g. "Reading level"). */
  label: string
  dimension: VoiceDimensionKey
  status: CharacteristicChangeStatus
  /** 1–5 score on the parent, or null when the parent didn't score this characteristic. */
  beforeScore: number | null
  /** 1–5 score on the child, or null when the child didn't score it. */
  afterScore: number | null
  /** Whether the characteristic appeared as a finding (score ≤3) on the parent. */
  beforePresent: boolean
  /** Whether the characteristic appears as a finding (score ≤3) on the child. */
  afterPresent: boolean
  /** Confidence of the child assessment — carried through from finding.confidence. */
  confidence: 'high' | 'moderate' | 'low'
}

export interface LensComparison {
  scoreDelta: {
    cai: ScoreDeltaEntry
    dimensions: Record<VoiceDimensionKey, ScoreDeltaEntry>
  }

  predictionDelta: {
    understanding: PredictionDeltaEntry
    execution: PredictionDeltaEntry | null
  } | null

  /**
   * Per-characteristic change status for the redesigned comparison UI.
   * Keyed by characteristic key. Backward compatible — optional so existing
   * consumers of LensComparison ignore it.
   */
  characteristicChanges?: Record<string, CharacteristicChange>

  /** Findings in v1 that no longer appear in v2 — issues resolved. */
  resolvedFindings: LensFinding[]
  /** Findings present in both, after score higher — issue improved but not resolved. */
  improvedFindings: LensFinding[]
  /** Findings present in both v1 and v2, same score — unchanged issues. */
  unchangedFindings: LensFinding[]
  /** Findings present in both, after score lower — issue worsened. */
  regressedFindings: LensFinding[]
  /** Findings in v2 that weren't in v1 — new issues. */
  newFindings: LensFinding[]

  stats: {
    findingsResolved: number
    findingsImproved: number
    findingsUnchanged: number
    findingsRegressed: number
    findingsNew: number
    /** resolved - new; positive = net improvement */
    netImprovement: number
  }
}

/**
 * Pipeline-neutral persisted revision comparison. Producers may expose their
 * native LensComparison fields as well, but consumers must use these outcome
 * buckets when deciding whether a revision improved.
 */
export interface LensRevisionComparison {
  contractVersion: 'lens.revision-comparison.v1'
  status: 'completed' | 'rejected_too_different' | 'uncertain' | 'unsupported' | 'failed'
  relation: 'related' | 'too_different' | 'uncertain'
  contentSimilarity: {
    score: number
    sharedTokenRatio: number
    parentTokens: number
    childTokens: number
    evidence: string
  }
  scoreDelta: {
    overall: number | null
    dimensions: Record<string, number | null>
  }
  findings: Array<{
    outcome: string
    parent: unknown
    child: unknown
    matchMethod: string
    confidence: string
    evidence: string
  }>
  unresolvedFindings: unknown[]
  movedOrChangedFindings: unknown[]
  resolvedFindings: unknown[]
  uncertainFindings: unknown[]
  newFindings: unknown[]
  stats: {
    unresolved: number
    movedOrChanged: number
    resolved: number
    uncertain: number
    new: number
  }
  reason?: string
}
